import type { Volunteer, VolunteerDay, Task } from '@nepal-football/shared';

const DAYS: VolunteerDay[] = ['Friday', 'Saturday', 'Sunday'];

export interface VolunteerRowProps {
  volunteer: Volunteer;
  isAdmin: boolean;
  tasksById?: Map<string, Task>;
  onEdit: (v: Volunteer) => void;
  onDelete: (id: string) => void;
}

function DayCell({ available }: { available: boolean }) {
  return (
    <td className="vol-day-cell" aria-label={available ? 'Available' : 'Not available'}>
      <span className={`vol-day-badge${available ? ' vol-day-yes' : ' vol-day-no'}`}>
        {available ? '✓' : '–'}
      </span>
    </td>
  );
}

export default function VolunteerRow({
  volunteer,
  isAdmin,
  tasksById,
  onEdit,
  onDelete,
}: VolunteerRowProps) {
  const dayLookup = new Set(volunteer.days);
  const taskNames = (volunteer.taskIds ?? [])
    .map((id) => tasksById?.get(id)?.task)
    .filter((s): s is string => typeof s === 'string');

  function handleDelete() {
    const ok = window.confirm(`Remove ${volunteer.name} from the volunteer list?`);
    if (ok) onDelete(volunteer.id);
  }

  return (
    <tr className="vol-row">
      <td className="vol-cell vol-name">
        <span className="vol-avatar">{volunteer.name.charAt(0).toUpperCase()}</span>
        <span>
          <div className="vol-name-text">{volunteer.name}</div>
          {volunteer.email && <div className="vol-city">{volunteer.email}</div>}
        </span>
      </td>
      {DAYS.map((day) => (
        <DayCell key={day} available={dayLookup.has(day)} />
      ))}
      <td className="vol-cell vol-tasks-cell">
        {taskNames.length > 0 ? (
          <div className="vol-task-tags">
            {taskNames.map((tn, i) => (
              <span key={i} className="vol-task-tag">{tn}</span>
            ))}
          </div>
        ) : (
          <span className="vol-muted">—</span>
        )}
      </td>
      <td className="vol-cell">
        {volunteer.city ? (
          <span className="vol-role-badge">{volunteer.city}</span>
        ) : (
          <span className="vol-muted">—</span>
        )}
      </td>
      <td className="vol-cell vol-muted vol-date">
        {new Date(volunteer.submittedAt).toLocaleDateString('en-GB', {
          day: 'numeric', month: 'short',
        })}
      </td>
      {isAdmin && (
        <td className="vol-cell vol-actions">
          <button
            type="button"
            className="icon-btn"
            onClick={() => onEdit(volunteer)}
            aria-label={`Edit ${volunteer.name}`}
          >
            ✏️
          </button>
          <button
            type="button"
            className="icon-btn"
            onClick={handleDelete}
            aria-label={`Delete ${volunteer.name}`}
          >
            🗑️
          </button>
        </td>
      )}
    </tr>
  );
}
